import React from 'react';
import styled from 'styled-components';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import Avatar from '../../avatar/Avatar';
import DropdownMenu from '../../dropdown-menu/DropdownMenu';
import StatusBadge from '../../status-badge/StatusBadge';
import { useIsUserOnline } from '../../../hooks/useIsUserOnline';
import {
    isFollowableMember,
    isPrimaryPosition,
} from '../../../utils/memberPositionUtils';
import MemberFollowButton from './MemberFollowButton';

const UserItem = styled.div.attrs({
    className: "u-row-between u-gap16",
})`
    padding: 8px;
    background: #fff;
    border-radius: 4px;
    width: 100%;
    touch-action: none;
    user-select: none;
    opacity: ${props => props.$isDragging ? 0.5 : 1};
`;

const DragHandle = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
    cursor: ${props => props.$isDragging ? 'grabbing' : 'grab'};
`;

const UserInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const UserActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: default;
`;

const InviteActionButton = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid #f0f0f0;
    background: #fcfcfc;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
        background: #f0f0f0;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .material-symbols-rounded {
        font-size: 16px;
    }
`;

const SortableUserItem = ({
    membership,
    currentUserId,
    getMenuItems,
    canControl = false,
    onResendInvitation,
    onCancelInvitation,
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: membership._key, disabled: !!membership.is_invited });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const { position, is_invited } = membership;
    const isPrimary = isPrimaryPosition(position);
    const menuItems = getMenuItems(membership);
    const isOnline = useIsUserOnline(membership.user_id);

    const showFollowButton = isFollowableMember(membership, currentUserId);

    return (
        <UserItem
            ref={setNodeRef}
            style={style}
            $isDragging={isDragging}
        >
            <DragHandle
                $isDragging={isDragging}
                {...attributes}
                {...listeners}
            >
                {is_invited ? (
                    <div>
                        <StatusBadge 
                            label="招待中" 
                            color="border"
                        />
                    </div>
                ) : (
                    <div style={!isPrimary ? { marginLeft: 16 } : undefined}>
                        <Avatar
                            src={membership.avatar_url}
                            size={isPrimary ? 48 : 32}
                            alt={membership.display_name_jp}
                            name={membership.display_name_jp}
                            isOnline={isOnline}
                        />
                    </div>
                )}
                
                <UserInfo>
                    {is_invited ? (
                        <div className="bodytext-11 u-text-light-gray u-line-height-100">{membership.email || "招待済みユーザー"}</div>
                    ) : (
                        <>
                            <div className="heading-spacing-13 u-line-height-100">{membership.display_name_jp}</div>
                            <div className="bodytext-11 u-line-height-100 u-text-light-gray">
                                {membership.title_jp}{isPrimary ? "（責任者）" : ""}
                            </div>
                            {isPrimary && membership.affiliation_jp && (
                                <div className="bodytext-11 u-line-height-100 u-text-light-gray">
                                    {membership.affiliation_jp}
                                </div>
                            )}
                        </>
                    )}
                </UserInfo>
            </DragHandle>
            <UserActions>
                {is_invited && canControl ? (
                    <>
                        <InviteActionButton onClick={() => onResendInvitation?.(membership)}>
                            <span className="material-symbols-rounded">send</span>
                            <span className="bodytext-11">再送信</span>
                        </InviteActionButton>
                        <InviteActionButton onClick={() => onCancelInvitation?.(membership)}>
                            <span className="material-symbols-rounded">close</span>
                            <span className="bodytext-11">取り消し</span>
                        </InviteActionButton>
                    </>
                ) : (
                    <>
                        {showFollowButton && (
                            <MemberFollowButton
                                userId={membership.user_id}
                                initialIsFollowing={!!membership.is_following}
                            />
                        )}
                        {!is_invited && canControl && menuItems.length > 0 && (
                            <DropdownMenu items={menuItems} />
                        )}
                    </>
                )}
            </UserActions>
        </UserItem>
    );
};

export default SortableUserItem;
